import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { getCsrfToken } from "shared/form_helpers"

export default class extends Controller {
  static targets = [
    "dataSource", 
    "fields", 
    "filters", 
    "preview",
    "fieldSearch",
    "fieldList",
    "selectedFields",
    "filterContainer",
    "previewTable",
    "saveButton",
    "templateName",
    "templateDescription",
    "chartBuilder"
  ]

  static values = {
    availableDataSources: Array,
    selectedDataSource: String,
    reportConfiguration: Object
  }

  connect() {
    this.initializeConfiguration()
    this.loadAvailableDataSources()
    this.initializeDragAndDrop()
  }

  initializeConfiguration() {
    this.reportConfigurationValue = {
      dataSource: null,
      fields: [],
      filters: [],
      sorting: [],
      grouping: [],
      chart: {
        enabled: false,
        type: null,
        dimensions: [],
        measures: [],
        configuration: {}
      }
    }
  }

  // Data Source Selection
  async loadAvailableDataSources() {
    try {
      const response = await fetch('/api/reports/data_sources', {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        }
      })
      
      
      if (response.ok) {
        const dataSources = await response.json()
        this.availableDataSourcesValue = dataSources
        this.renderDataSourceOptions()
      } else {
        const errorText = await response.text()
        console.error('Data sources request failed:', response.status, errorText)
        this.showError(`Failed to load data sources: ${response.status} ${response.statusText}`)
      }
    } catch (error) {
      console.error('Failed to load data sources:', error)
      this.showError('Failed to load available data sources')
    }
  }

  renderDataSourceOptions() {
    if (!this.hasDataSourceTarget) {
      console.error('Data source target not found')
      return
    }

    const options = this.availableDataSourcesValue.map(source => 
      `<option value="${source.name}">${source.name}</option>`
    ).join('')

    this.dataSourceTarget.innerHTML = `
      <option value="">Select a data source...</option>
      ${options}
    `
  }

  async selectDataSource(event) {
    const dataSourceName = event.target.value
    
    
    if (!dataSourceName) {
      this.clearFields()
      return
    }

    this.selectedDataSourceValue = dataSourceName
    
    // Create new configuration object to trigger Stimulus reactivity
    const updatedConfig = {
      ...this.reportConfigurationValue,
      dataSource: dataSourceName
    }
    
    this.reportConfigurationValue = updatedConfig
    
    await this.loadDataSourceFields(dataSourceName)
    this.clearFilters()
    this.initializeFilterBuilder()
    this.clearPreview()
  }

  async loadDataSourceFields(dataSourceName) {
    try {
      const response = await fetch(`/api/reports/data_sources/${dataSourceName}/fields`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        }
      })
      
      
      if (response.ok) {
        const fields = await response.json()
        this.renderFieldPicker(fields)
      } else {
        console.error('Failed to load fields, status:', response.status)
        const errorText = await response.text()
        console.error('Error response:', errorText)
      }
    } catch (error) {
      console.error('Failed to load fields:', error)
      this.showError('Failed to load fields for selected data source')
    }
  }

  renderFieldPicker(fields) {
    
    if (!this.hasFieldListTarget) {
      console.error('No fieldList target found!')
      return
    }

    const fieldItems = fields.map(field => `
      <div class="field-item" 
           data-field-name="${field.name}" 
           data-field-type="${field.type}"
           draggable="true"
           data-action="dragstart->report-builder#handleFieldDragStart">
        <div class="form-check">
          <input class="form-check-input" type="checkbox" 
                 id="field_${field.name}" 
                 data-action="change->report-builder#toggleField"
                 data-field-name="${field.name}">
          <label class="form-check-label" for="field_${field.name}">
            <i class="fas fa-grip-vertical text-muted me-2" style="cursor: grab;"></i>
            <strong>${field.display_name}</strong>
            <small class="text-muted d-block">${field.description}</small>
            <span class="badge bg-secondary">${field.type}</span>
          </label>
        </div>
      </div>
    `).join('')

    this.fieldListTarget.innerHTML = fieldItems
  }

  // Field Search Functionality
  searchFields(event) {
    const searchTerm = event.target.value.toLowerCase()
    const fieldItems = this.fieldListTarget.querySelectorAll('.field-item')
    
    fieldItems.forEach(item => {
      const fieldName = item.dataset.fieldName.toLowerCase()
      const label = item.querySelector('label').textContent.toLowerCase()
      
      if (fieldName.includes(searchTerm) || label.includes(searchTerm)) {
        item.style.display = 'block'
      } else {
        item.style.display = 'none'
      }
    })
  }

  // Field Selection Management
  toggleField(event) {
    const fieldName = event.target.dataset.fieldName
    const isChecked = event.target.checked
    
    
    if (isChecked) {
      this.addField(fieldName)
    } else {
      this.removeField(fieldName)
    }
    
    this.updateSelectedFieldsDisplay()
    this.updatePreview()
  }

  addField(fieldName) {
    const field = this.findFieldByName(fieldName)
    
    if (field && !this.reportConfigurationValue.fields.find(f => f.name === fieldName)) {
      const newField = {
        name: fieldName,
        display_name: field.display_name,
        type: field.type,
        formatting: this.getDefaultFormatting(field.type)
      }
      
      // Create a new configuration object to trigger Stimulus reactivity
      const updatedConfig = {
        ...this.reportConfigurationValue,
        fields: [...this.reportConfigurationValue.fields, newField]
      }
      
      this.reportConfigurationValue = updatedConfig
    } else {
    }
  }

  removeField(fieldName) {
    
    // Create a new configuration object to trigger Stimulus reactivity
    const updatedConfig = {
      ...this.reportConfigurationValue,
      fields: this.reportConfigurationValue.fields.filter(field => field.name !== fieldName)
    }
    
    this.reportConfigurationValue = updatedConfig
  }

  findFieldByName(fieldName) {
    const fieldItems = this.fieldListTarget.querySelectorAll('.field-item')
    for (const item of fieldItems) {
      if (item.dataset.fieldName === fieldName) {
        const label = item.querySelector('label')
        return {
          name: fieldName,
          display_name: label.querySelector('strong').textContent,
          type: item.dataset.fieldType,
          description: label.querySelector('small').textContent
        }
      }
    }
    return null
  }

  getDefaultFormatting(fieldType) {
    switch (fieldType) {
      case 'currency':
        return { type: 'currency', symbol: '$', decimals: 2 }
      case 'date':
        return { type: 'date', format: 'MM/DD/YYYY' }
      case 'datetime':
        return { type: 'datetime', format: 'MM/DD/YYYY HH:mm' }
      case 'number':
        return { type: 'number', decimals: 0 }
      default:
        return { type: 'text' }
    }
  }

  updateSelectedFieldsDisplay() {
    
    if (!this.hasSelectedFieldsTarget) {
      console.error('No selectedFields target found!')
      return
    }

    const selectedFields = this.reportConfigurationValue.fields.map((field, index) => `
      <div class="selected-field-item d-flex justify-content-between align-items-center mb-2" 
           data-field-name="${field.name}"
           data-field-index="${index}"
           draggable="true"
           data-action="dragstart->report-builder#handleSelectedFieldDragStart dragover->report-builder#handleDragOver drop->report-builder#handleSelectedFieldDrop">
        <div class="d-flex align-items-center">
          <i class="fas fa-grip-vertical text-muted me-2" style="cursor: grab;"></i>
          <div>
            <strong>${field.display_name}</strong>
            <small class="text-muted d-block">${field.name}</small>
          </div>
        </div>
        <div class="btn-group btn-group-sm">
          <button type="button" class="btn btn-outline-secondary" 
                  data-action="click->report-builder#configureField"
                  data-field-name="${field.name}">
            Configure
          </button>
          <button type="button" class="btn btn-outline-danger" 
                  data-action="click->report-builder#removeSelectedField"
                  data-field-name="${field.name}">
            Remove
          </button>
        </div>
      </div>
    `).join('')

    
    const finalHTML = selectedFields || '<p class="text-muted">No fields selected</p>'
    this.selectedFieldsTarget.innerHTML = finalHTML
    
    
    // Re-initialize drag and drop for selected fields
    this.initializeSelectedFieldsDragDrop()
  }

  removeSelectedField(event) {
    const fieldName = event.target.dataset.fieldName
    this.removeField(fieldName)
    
    // Uncheck the field in the picker
    const checkbox = this.fieldListTarget.querySelector(`input[data-field-name="${fieldName}"]`)
    if (checkbox) {
      checkbox.checked = false
    }
    
    this.updateSelectedFieldsDisplay()
    this.updatePreview()
  }

  // Field Configuration
  configureField(event) {
    const fieldName = event.target.dataset.fieldName
    const field = this.reportConfigurationValue.fields.find(f => f.name === fieldName)
    
    if (!field) return

    // Set up the field configuration modal
    const modal = document.getElementById('fieldConfigurationModal')
    const modalTitle = modal.querySelector('.modal-title')
    const fieldConfigController = modal.querySelector('[data-controller="field-configuration"]')
    
    modalTitle.textContent = `Configure Field: ${field.display_name}`
    
    // Set field configuration values
    if (fieldConfigController) {
      fieldConfigController.setAttribute('data-field-configuration-field-name-value', field.name)
      fieldConfigController.setAttribute('data-field-configuration-field-type-value', field.type)
      fieldConfigController.setAttribute('data-field-configuration-configuration-value', JSON.stringify({
        display_name: field.display_name,
        formatting: field.formatting,
        sorting: field.sorting || { enabled: false, order: 'asc' },
        grouping: field.grouping || { enabled: false },
        display: field.display || { width: 'auto', alignment: 'left' }
      }))
    }

    // Show the modal
    const bootstrapModal = new Modal(modal)
    bootstrapModal.show()

    // Listen for configuration changes
    const handleConfigurationChange = (event) => {
      const { fieldName: configFieldName, configuration } = event.detail
      if (configFieldName === fieldName) {
        // Update the field configuration
        const fieldIndex = this.reportConfigurationValue.fields.findIndex(f => f.name === fieldName)
        if (fieldIndex !== -1) {
          this.reportConfigurationValue.fields[fieldIndex] = {
            ...this.reportConfigurationValue.fields[fieldIndex],
            display_name: configuration.display_name,
            formatting: configuration.formatting,
            sorting: configuration.sorting,
            grouping: configuration.grouping,
            display: configuration.display
          }
          this.updateSelectedFieldsDisplay()
          this.updatePreview()
        }
      }
    }

    // Add event listener for configuration changes
    modal.addEventListener('field-configuration-changed', handleConfigurationChange)

    // Clean up event listener when modal is hidden
    modal.addEventListener('hidden.bs.modal', () => {
      modal.removeEventListener('field-configuration-changed', handleConfigurationChange)
    }, { once: true })
  }

  // Preview Functionality
  async updatePreview() {
    
    if (!this.hasPreviewTarget || !this.reportConfigurationValue.dataSource || 
        this.reportConfigurationValue.fields.length === 0) {
      this.clearPreview()
      return
    }
    

    try {
      
      const response = await fetch('/api/reports/preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify({
          configuration: this.reportConfigurationValue,
          limit: 10 // Preview only first 10 rows
        })
      })
      
      
      if (response.ok) {
        const previewData = await response.json()
        this.currentReportData = previewData.rows || []
        this.renderPreview(previewData)
        this.updateChartWithReportData()
      } else {
        const errorText = await response.text()
        console.error('Preview request failed:', response.status, errorText)
        this.showError('Failed to generate report preview: ' + response.status)
      }
    } catch (error) {
      console.error('Failed to generate preview:', error)
      this.showError('Failed to generate report preview')
    }
  }

  renderPreview(previewData) {
    if (!previewData.rows || previewData.rows.length === 0) {
      this.previewTarget.innerHTML = '<p class="text-muted">No data available for preview</p>'
      return
    }

    const headers = this.reportConfigurationValue.fields.map(field => 
      `<th>${field.display_name}</th>`
    ).join('')

    const rows = previewData.rows.map(row => {
      const cells = this.reportConfigurationValue.fields.map(field => {
        const value = row[field.name]
        const formattedValue = this.formatValue(value, field.formatting)
        return `<td>${formattedValue || ''}</td>`
      }).join('')
      return `<tr>${cells}</tr>`
    }).join('')

    this.previewTarget.innerHTML = `
      <div class="table-responsive">
        <table class="table table-sm table-striped">
          <thead class="table-dark">
            <tr>${headers}</tr>
          </thead>
          <tbody>
            ${rows}
          </tbody>
        </table>
        <small class="text-muted">Showing first ${previewData.rows.length} rows</small>
      </div>
    `
  }

  formatValue(value, formatting) {
    if (value === null || value === undefined) return ''
    
    switch (formatting.type) {
      case 'currency':
        return `${formatting.symbol}${parseFloat(value).toFixed(formatting.decimals)}`
      case 'date':
        return new Date(value).toLocaleDateString()
      case 'datetime':
        return new Date(value).toLocaleString()
      case 'number':
        return parseFloat(value).toFixed(formatting.decimals)
      default:
        return value.toString()
    }
  }

  clearPreview() {
    if (this.hasPreviewTarget) {
      this.previewTarget.innerHTML = '<p class="text-muted">Configure your report to see a preview</p>'
    }
  }

  clearFields() {
    if (this.hasFieldListTarget) {
      this.fieldListTarget.innerHTML = ''
    }
    if (this.hasSelectedFieldsTarget) {
      this.selectedFieldsTarget.innerHTML = '<p class="text-muted">No fields selected</p>'
    }
    this.reportConfigurationValue.fields = []
  }

  clearFilters() {
    if (this.hasFilterContainerTarget) {
      this.filterContainerTarget.innerHTML = '<p class="text-muted">No filters configured</p>'
    }
    this.reportConfigurationValue.filters = []
  }

  // Filter Management
  initializeFilterBuilder() {
    if (!this.hasFilterContainerTarget || !this.reportConfigurationValue.dataSource) return

    // Create filter builder container
    this.filterContainerTarget.innerHTML = `
      <div data-controller="filter-builder" 
           data-filter-builder-available-fields-value='${JSON.stringify(this.getAvailableFieldsForFilters())}'
           data-filter-builder-filters-value='${JSON.stringify(this.reportConfigurationValue.filters)}'
           data-action="filters-changed->report-builder#handleFiltersChanged">
        <div data-filter-builder-target="filterContainer"></div>
        <button type="button" class="btn btn-outline-primary btn-sm mt-2" 
                data-action="click->filter-builder#addFilter">
          <i class="fas fa-plus"></i> Add Filter
        </button>
      </div>
    `
  }

  getAvailableFieldsForFilters() {
    // Get fields from the current data source that are suitable for filtering
    const fieldItems = this.fieldListTarget ? this.fieldListTarget.querySelectorAll('.field-item') : []
    const availableFields = []

    fieldItems.forEach(item => {
      const fieldName = item.dataset.fieldName
      const fieldType = item.dataset.fieldType
      const label = item.querySelector('label strong')?.textContent || fieldName

      availableFields.push({
        name: fieldName,
        display_name: label,
        type: fieldType
      })
    })

    return availableFields
  }

  handleFiltersChanged(event) {
    const { filters, logicOperator, isValid } = event.detail
    
    this.reportConfigurationValue.filters = filters
    this.reportConfigurationValue.filterLogic = logicOperator

    // Update preview if filters are valid
    if (isValid) {
      this.updatePreview()
    }
  }

  addFilter() {
    // This method is called when the "Add Filter" button is clicked
    // The actual filter addition is handled by the filter-builder controller
    this.initializeFilterBuilder()
  }

  clearAllFilters() {
    this.reportConfigurationValue.filters = []
    this.reportConfigurationValue.filterLogic = 'AND'
    this.initializeFilterBuilder()
    this.updatePreview()
  }

  // Generate Full Report
  async generateFullReport() {
    if (!this.reportConfigurationValue.dataSource || 
        this.reportConfigurationValue.fields.length === 0) {
      this.showError('Please configure your report before generating')
      return
    }

    try {
      const response = await fetch('/api/reports/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify({
          configuration: this.reportConfigurationValue
        })
      })
      
      if (response.ok) {
        const result = await response.json()
        this.showSuccess('Report generated successfully')
        // Redirect to report view or download
        if (result.download_url) {
          window.open(result.download_url, '_blank')
        }
      } else {
        const error = await response.json()
        this.showError(error.message || 'Failed to generate report')
      }
    } catch (error) {
      console.error('Failed to generate report:', error)
      this.showError('Failed to generate report')
    }
  }

  // Template Management
  async saveTemplate(event) {
    event.preventDefault()
    
    if (!this.hasTemplateNameTarget || !this.templateNameTarget.value.trim()) {
      this.showError('Please enter a template name')
      return
    }

    const templateData = {
      name: this.templateNameTarget.value.trim(),
      description: this.hasTemplateDescriptionTarget ? this.templateDescriptionTarget.value.trim() : '',
      configuration: this.reportConfigurationValue
    }

    try {
      const response = await fetch('/api/reports/templates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify(templateData)
      })
      
      if (response.ok) {
        this.showSuccess('Template saved successfully')
        this.clearTemplateForm()
      } else {
        const error = await response.json()
        this.showError(error.message || 'Failed to save template')
      }
    } catch (error) {
      console.error('Failed to save template:', error)
      this.showError('Failed to save template')
    }
  }

  clearTemplateForm() {
    if (this.hasTemplateNameTarget) {
      this.templateNameTarget.value = ''
    }
    if (this.hasTemplateDescriptionTarget) {
      this.templateDescriptionTarget.value = ''
    }
  }

  // Drag and Drop Functionality
  initializeDragAndDrop() {
    // Set up drop zone for selected fields
    if (this.hasSelectedFieldsTarget) {
      this.selectedFieldsTarget.addEventListener('dragover', this.handleDragOver.bind(this))
      this.selectedFieldsTarget.addEventListener('drop', this.handleFieldDrop.bind(this))
      
      // Add touch support for mobile devices
      this.initializeTouchSupport()
    }
  }

  initializeTouchSupport() {
    // Add touch event listeners for mobile drag and drop simulation
    let touchStartElement = null
    let touchStartPosition = null

    document.addEventListener('touchstart', (e) => {
      const fieldItem = e.target.closest('.field-item, .selected-field-item')
      if (fieldItem && fieldItem.draggable) {
        touchStartElement = fieldItem
        touchStartPosition = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY
        }
        
        // Add visual feedback
        fieldItem.classList.add('touch-dragging')
      }
    }, { passive: true })

    document.addEventListener('touchmove', (e) => {
      if (touchStartElement) {
        e.preventDefault()
        
        // Update visual feedback
        const currentPosition = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY
        }
        
        const deltaX = currentPosition.x - touchStartPosition.x
        const deltaY = currentPosition.y - touchStartPosition.y
        
        touchStartElement.style.transform = `translate(${deltaX}px, ${deltaY}px) rotate(2deg)`
        touchStartElement.style.opacity = '0.7'
        touchStartElement.style.zIndex = '1000'
      }
    })

    document.addEventListener('touchend', (e) => {
      if (touchStartElement) {
        // Reset visual state
        touchStartElement.classList.remove('touch-dragging')
        touchStartElement.style.transform = ''
        touchStartElement.style.opacity = ''
        touchStartElement.style.zIndex = ''
        
        // Find drop target
        const dropTarget = document.elementFromPoint(
          e.changedTouches[0].clientX,
          e.changedTouches[0].clientY
        )
        
        if (dropTarget) {
          const selectedFieldsContainer = dropTarget.closest('[data-report-builder-target="selectedFields"]')
          
          if (selectedFieldsContainer && touchStartElement.classList.contains('field-item')) {
            // Simulate field addition
            const fieldName = touchStartElement.dataset.fieldName
            this.addField(fieldName)
            
            const checkbox = touchStartElement.querySelector('input[type="checkbox"]')
            if (checkbox) {
              checkbox.checked = true
            }
            
            this.updateSelectedFieldsDisplay()
            this.updatePreview()
            
            // Show success feedback
            this.showTouchFeedback('Field added to report', 'success')
          }
        }
        
        touchStartElement = null
        touchStartPosition = null
      }
    })
  }

  showTouchFeedback(message, type = 'info') {
    // Create temporary feedback message for touch interactions
    const feedback = document.createElement('div')
    feedback.className = `alert alert-${type} position-fixed`
    feedback.style.cssText = `
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 9999;
      min-width: 200px;
      text-align: center;
      animation: slideDown 0.3s ease-out;
    `
    feedback.textContent = message
    
    document.body.appendChild(feedback)
    
    setTimeout(() => {
      feedback.style.animation = 'slideUp 0.3s ease-in'
      setTimeout(() => {
        if (feedback.parentNode) {
          feedback.remove()
        }
      }, 300)
    }, 2000)
  }

  initializeFieldDragDrop() {
    const fieldItems = this.fieldListTarget.querySelectorAll('.field-item')
    fieldItems.forEach(item => {
      // The dragstart event should already be handled by Stimulus data-action
      // But let's make sure the checkboxes work
      const checkbox = item.querySelector('input[type="checkbox"]')
      if (checkbox) {
        // Remove any existing listeners to avoid duplicates
        checkbox.removeEventListener('change', this.handleCheckboxChange)
        // Add the event listener
        checkbox.addEventListener('change', this.handleCheckboxChange.bind(this))
      }
    })
  }

  handleCheckboxChange(event) {
    this.toggleField(event)
  }

  initializeSelectedFieldsDragDrop() {
    // This method is called after updating selected fields display
    // to ensure drag and drop works for dynamically created elements
    const selectedFieldItems = this.selectedFieldsTarget.querySelectorAll('.selected-field-item')
    selectedFieldItems.forEach(item => {
      item.addEventListener('dragover', this.handleDragOver.bind(this))
      item.addEventListener('drop', this.handleSelectedFieldDrop.bind(this))
    })
  }

  handleFieldDragStart(event) {
    const fieldName = event.target.closest('.field-item').dataset.fieldName
    const fieldType = event.target.closest('.field-item').dataset.fieldType
    
    
    event.dataTransfer.setData('text/plain', JSON.stringify({
      type: 'field',
      fieldName: fieldName,
      fieldType: fieldType
    }))
    
    event.dataTransfer.effectAllowed = 'copy'
    event.target.closest('.field-item').classList.add('dragging')
  }

  handleSelectedFieldDragStart(event) {
    const fieldItem = event.target.closest('.selected-field-item')
    const fieldName = fieldItem.dataset.fieldName
    const fieldIndex = parseInt(fieldItem.dataset.fieldIndex)
    
    event.dataTransfer.setData('text/plain', JSON.stringify({
      type: 'selected-field',
      fieldName: fieldName,
      fieldIndex: fieldIndex
    }))
    
    event.dataTransfer.effectAllowed = 'move'
    fieldItem.classList.add('dragging')
  }

  handleDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
    
    // Add visual feedback
    const dropZone = event.currentTarget
    if (dropZone === this.selectedFieldsTarget) {
      dropZone.classList.add('drag-over')
    }
  }

  handleFieldDrop(event) {
    event.preventDefault()
    
    try {
      const dragData = JSON.parse(event.dataTransfer.getData('text/plain'))
      
      if (dragData.type === 'field') {
        // Add field to selected fields
        this.addField(dragData.fieldName)
        
        // Check the corresponding checkbox
        const checkbox = this.fieldListTarget.querySelector(`input[data-field-name="${dragData.fieldName}"]`)
        if (checkbox) {
          checkbox.checked = true
        }
        
        this.updateSelectedFieldsDisplay()
        this.updatePreview()
      }
    } catch (error) {
      console.error('Error handling field drop:', error)
    }
    
    // Clean up visual feedback
    this.selectedFieldsTarget.classList.remove('drag-over')
    this.cleanupDragState()
  }

  handleSelectedFieldDrop(event) {
    event.preventDefault()
    
    try {
      const dragData = JSON.parse(event.dataTransfer.getData('text/plain'))
      
      if (dragData.type === 'selected-field') {
        const dropTarget = event.target.closest('.selected-field-item')
        if (!dropTarget) return
        
        const targetIndex = parseInt(dropTarget.dataset.fieldIndex)
        const sourceIndex = dragData.fieldIndex
        
        if (sourceIndex !== targetIndex) {
          
          // Reorder the fields array
          const fields = [...this.reportConfigurationValue.fields]
          const [movedField] = fields.splice(sourceIndex, 1)
          fields.splice(targetIndex, 0, movedField)
          
          
          // Create new configuration object to trigger Stimulus reactivity
          const updatedConfig = {
            ...this.reportConfigurationValue,
            fields: fields
          }
          
          this.reportConfigurationValue = updatedConfig
          this.updateSelectedFieldsDisplay()
          this.updatePreview()
        }
      }
    } catch (error) {
      console.error('Error handling selected field drop:', error)
    }
    
    this.cleanupDragState()
  }

  cleanupDragState() {
    // Remove dragging class from all items
    const draggingItems = document.querySelectorAll('.dragging')
    draggingItems.forEach(item => item.classList.remove('dragging'))
    
    // Remove drag-over class
    const dragOverItems = document.querySelectorAll('.drag-over')
    dragOverItems.forEach(item => item.classList.remove('drag-over'))
  }

  // Chart Integration
  handleChartConfigurationChanged(event) {
    const { configuration, isValid } = event.detail
    
    this.reportConfigurationValue.chart = {
      enabled: isValid,
      ...configuration
    }
    
    // Update chart with current report data if available
    if (isValid && this.currentReportData) {
      this.updateChartWithReportData()
    }
  }

  updateChartWithReportData() {
    // Send current report data to chart builder
    const chartBuilderElement = this.element.querySelector('[data-controller="chart-builder"]')
    if (chartBuilderElement) {
      const event = new CustomEvent('report-data-updated', {
        detail: {
          reportData: this.currentReportData,
          availableFields: this.getAvailableFieldsForChart()
        },
        bubbles: true
      })
      chartBuilderElement.dispatchEvent(event)
    }
  }

  getAvailableFieldsForChart() {
    // Get fields from the current data source that are suitable for charts
    const fieldItems = this.fieldListTarget ? this.fieldListTarget.querySelectorAll('.field-item') : []
    const availableFields = []

    fieldItems.forEach(item => {
      const fieldName = item.dataset.fieldName
      const fieldType = item.dataset.fieldType
      const label = item.querySelector('label strong')?.textContent || fieldName

      availableFields.push({
        name: fieldName,
        display_name: label,
        type: fieldType
      })
    })

    return availableFields
  }

  // Template Integration Methods
  getConfiguration() {
    // Return current report configuration for template saving
    return this.reportConfigurationValue
  }

  loadConfiguration(configuration) {
    // Load a template configuration into the report builder
    if (!configuration) return

    // Update the configuration
    this.reportConfigurationValue = {
      dataSource: configuration.dataSource || configuration.data_source,
      fields: configuration.fields || [],
      filters: configuration.filters || [],
      sorting: configuration.sorting || [],
      grouping: configuration.grouping || [],
      chart: configuration.chart || {
        enabled: false,
        type: null,
        dimensions: [],
        measures: [],
        configuration: {}
      }
    }

    // Update UI to reflect loaded configuration
    this.applyConfigurationToUI()
  }

  async applyConfigurationToUI() {
    const config = this.reportConfigurationValue

    // Set data source
    if (config.dataSource && this.hasDataSourceTarget) {
      this.dataSourceTarget.value = config.dataSource
      this.selectedDataSourceValue = config.dataSource
      
      // Load fields for the data source
      await this.loadDataSourceFields(config.dataSource)
      
      // Initialize filter builder
      this.initializeFilterBuilder()
    }

    // Set selected fields
    if (config.fields && config.fields.length > 0) {
      // Check corresponding checkboxes in field picker
      config.fields.forEach(field => {
        const checkbox = this.fieldListTarget?.querySelector(`input[data-field-name="${field.name}"]`)
        if (checkbox) {
          checkbox.checked = true
        }
      })
      
      // Update selected fields display
      this.updateSelectedFieldsDisplay()
    }

    // Apply filters
    if (config.filters && config.filters.length > 0) {
      // The filter builder controller will handle this when it's initialized
      setTimeout(() => {
        const filterBuilderElement = this.element.querySelector('[data-controller="filter-builder"]')
        if (filterBuilderElement) {
          const event = new CustomEvent('load-filters', {
            detail: { filters: config.filters, logicOperator: config.filterLogic || 'AND' },
            bubbles: true
          })
          filterBuilderElement.dispatchEvent(event)
        }
      }, 100)
    }

    // Apply chart configuration
    if (config.chart && config.chart.enabled) {
      setTimeout(() => {
        const chartBuilderElement = this.element.querySelector('[data-controller="chart-builder"]')
        if (chartBuilderElement) {
          const event = new CustomEvent('load-chart-configuration', {
            detail: { configuration: config.chart },
            bubbles: true
          })
          chartBuilderElement.dispatchEvent(event)
        }
      }, 100)
    }

    // Update preview
    setTimeout(() => {
      this.updatePreview()
    }, 200)
  }

  validateConfiguration() {
    // Validate current configuration before saving as template
    const config = this.reportConfigurationValue
    const errors = []

    if (!config.dataSource) {
      errors.push('Data source is required')
    }

    if (!config.fields || config.fields.length === 0) {
      errors.push('At least one field must be selected')
    }

    // Validate filters
    if (config.filters && config.filters.length > 0) {
      config.filters.forEach((filter, index) => {
        if (!filter.field) {
          errors.push(`Filter ${index + 1}: Field is required`)
        }
        if (filter.value === null || filter.value === undefined || filter.value === '') {
          errors.push(`Filter ${index + 1}: Value is required`)
        }
      })
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    }
  }

  // Utility Methods
  showError(message) {
    // Create or update error alert
    this.showAlert(message, 'danger')
  }

  showSuccess(message) {
    // Create or update success alert
    this.showAlert(message, 'success')
  }

  showAlert(message, type) {
    // Remove existing alerts
    const existingAlerts = this.element.querySelectorAll('.alert')
    existingAlerts.forEach(alert => alert.remove())

    // Create new alert
    const alert = document.createElement('div')
    alert.className = `alert alert-${type} alert-dismissible fade show`
    alert.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `
    
    // Insert at the top of the controller element
    this.element.insertBefore(alert, this.element.firstChild)
    
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      if (alert.parentNode) {
        alert.remove()
      }
    }, 5000)
  }
}